import { Fragment } from "react";
import "@/components/skeleton/dashboard-skeleton.css";

/** A single shimmer bar (mirrors the shared skeleton primitive). */
function Bar({ w, h = 12, r = 6, style }: { w: number | string; h?: number; r?: number; style?: React.CSSProperties }) {
  return <span className="sk" style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

/**
 * Loader for the All Metrics By Month / Week / Day grid — the same shimmer as
 * `BranchTablePanelSkeleton`, reshaped for the period table: a fixed Metric
 * column plus `cols` evenly-split period columns, broken up by group header
 * rows. Reuses the real table's classes and column widths so the grid drops in
 * without a layout shift.
 *
 * Renders bare (no card chrome) — the section's bordered container supplies it.
 */
export default function AllMetricsPeriodSkeleton({
  cols = 12,
  groups = 2,
  rows = 4,
}: {
  /** Period columns; defaults to the 12 intervals the API is asked for. */
  cols?: number;
  groups?: number;
  rows?: number;
}) {
  const colArr = Array.from({ length: Math.max(cols, 1) });
  const groupArr = Array.from({ length: groups });
  const rowArr = Array.from({ length: rows });
  const colW = `${78 / Math.max(cols, 1)}%`;

  return (
    <table className="dl-tbl am-grid-period" style={{ tableLayout: "fixed" }} aria-hidden="true">
      <thead>
        <tr>
          <th style={{ width: "22%" }}><Bar w={54} h={10} /></th>
          {colArr.map((_, i) => (
            <th key={i} style={{ width: colW, textAlign: "right" }}>
              <Bar w={30} h={9} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {groupArr.map((_, g) => (
          <Fragment key={g}>
            <tr className="am-group-head">
              <td colSpan={1 + colArr.length}>
                <div className="am-group-inner">
                  {/* Same 3×14 tick as `am-group-accent`, but drawn as a shimmer
                      bar — the real class paints it brand orange, which reads as
                      loaded content in an otherwise grey skeleton. */}
                  <Bar w={3} h={14} r={2} style={{ flexShrink: 0 }} />
                  <Bar w={132} h={10} />
                  <Bar w={26} h={14} r={20} />
                </div>
              </td>
            </tr>
            {rowArr.map((_, r) => (
              <tr key={r}>
                <td>
                  <div className="am-metric-name-cell">
                    <Bar w={r % 3 === 0 ? "62%" : "84%"} h={11} />
                  </div>
                </td>
                {colArr.map((_, c) => (
                  <td key={c} className="num" style={{ textAlign: "right" }}>
                    <Bar w={28} h={10} />
                  </td>
                ))}
              </tr>
            ))}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}
